<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Brand Navigation -->
    <BrandNav />
    
    <header v-if="showHeader" class="bg-primary shadow">
      <div class="container mx-auto px-4 py-3 flex items-center">
        <div class="flex items-center mr-auto md:ml-16 ml-0">
          <router-link to="/" class="no-underline">
            <img src="@/assets/it-meeder.png" alt="IT-Meeder Logo" class="h-24 md:h-28 w-auto mr-2 hover:opacity-90 transition-opacity" />
          </router-link>
        </div>
        <nav class="hidden md:block flex-grow">
          <ul class="flex space-x-8 justify-center">
            <li><router-link to="/" class="text-white hover:text-secondary transition-colors font-bold">Home</router-link></li>
            <li><router-link to="/services" class="text-white hover:text-secondary transition-colors font-bold">Services</router-link></li>
            <li><router-link to="/pricing" class="text-white hover:text-secondary transition-colors font-bold">Preise</router-link></li>
            
            <li><router-link to="/about" class="text-white hover:text-secondary transition-colors font-bold">Über uns</router-link></li>
            <li><router-link to="/contact" class="text-white hover:text-secondary transition-colors font-bold">Kontakt</router-link></li>
          </ul>
        </nav>
        
        <!-- Customer Login Button -->
        <div class="hidden md:block ml-4">
          <router-link to="/customer-login-coming-soon" class="bg-secondary text-primary px-4 py-2 rounded-md hover:bg-secondary-dark transition-colors font-bold">
            Kundenlogin
          </router-link>
        </div>
        
        <button @click="toggleMobileMenu" class="md:hidden text-white ml-auto p-2 focus:outline-none">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
        </button>
      </div>
      
      <!-- Mobile Dropdown Menu -->
      <div v-if="mobileMenuOpen" class="md:hidden bg-primary shadow-lg">
        <div class="container mx-auto px-4 py-2">
          <ul class="flex flex-col space-y-3 pb-4">
            <li><router-link @click="closeMobileMenu" to="/" class="text-white hover:text-secondary transition-colors font-bold block py-2">Home</router-link></li>
            <li><router-link @click="closeMobileMenu" to="/services" class="text-white hover:text-secondary transition-colors font-bold block py-2">Services</router-link></li>
            <li><router-link @click="closeMobileMenu" to="/pricing" class="text-white hover:text-secondary transition-colors font-bold block py-2">Preise</router-link></li>

            <li><router-link @click="closeMobileMenu" to="/about" class="text-white hover:text-secondary transition-colors font-bold block py-2">Über uns</router-link></li>
            <li><router-link @click="closeMobileMenu" to="/contact" class="text-white hover:text-secondary transition-colors font-bold block py-2">Kontakt</router-link></li>
            <li><router-link @click="closeMobileMenu" to="/customer-login-coming-soon" class="bg-secondary text-primary px-4 py-2 rounded-md hover:bg-secondary-dark transition-colors font-bold block text-center">Kundenlogin</router-link></li>
          </ul>
        </div>
      </div>
    </header>

    <main>
      <router-view />
    </main>

    <!-- Cookie Consent Banner -->
    <CookieConsent />

    <!-- Seitliches Popup für unvollständigen Funnel -->
    <div v-if="hasActiveFunnel && !isMobile" class="fixed right-0 bottom-32 z-50 transform transition-transform duration-500 ease-in-out" :class="{'translate-x-0': hasActiveFunnel, 'translate-x-full': !hasActiveFunnel}">
      <div class="bg-gradient-to-br from-white to-blue-50 shadow-xl rounded-l-lg border-l-4 border-primary p-5 max-w-xs animate-pulse-subtle">
        <div class="flex justify-between items-start mb-3">
          <h3 class="text-base font-bold text-primary flex items-center">
            <svg class="w-5 h-5 mr-2 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Bedarfsanalyse nicht abgeschlossen
          </h3>
          <button @click="dismissFunnelReminder" class="text-gray-400 hover:text-red-600 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="p-2 bg-yellow-50 border-l-4 border-yellow-400 rounded mb-4">
          <p class="text-sm text-gray-700">Sie haben Ihre Bedarfsanalyse noch nicht abgeschlossen. Möchten Sie zurückkehren und fortfahren?</p>
          <p class="text-sm text-gray-700 mt-2 font-medium">Hinweis: Beim Schließen dieses Dialogs werden alle eingegebenen Daten verworfen. Sie können das Formular jederzeit neu beginnen.</p>
        </div>
        <router-link :to="funnelPath" class="inline-block bg-secondary hover:bg-secondary-dark text-primary hover:text-primary-dark text-sm font-medium py-2 px-6 rounded-full shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1">
          Zurück zur Bedarfsanalyse
        </router-link>
      </div>
    </div>

    <footer v-if="showFooter" class="bg-primary text-white py-8 relative">
      <!-- Nach oben Pfeil -->
      <div class="absolute right-8 -top-5">
        <a href="#top" class="bg-secondary hover:bg-secondary-dark text-white p-2 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:-translate-y-1">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
          </svg>
        </a>
      </div>
      
      <div class="container mx-auto px-4">
        <div class="flex flex-col items-center mb-8">
          <div class="mb-8">
            <img src="@/assets/it-meeder.png" alt="IT-Meeder Logo" class="h-32 w-auto" />
          </div>
          
          <nav class="flex-grow text-center">
            <ul class="hidden md:flex flex-wrap justify-center space-x-8">
              <li><router-link to="/datenschutz" class="text-white hover:text-secondary transition-colors font-medium">Datenschutzerklärung</router-link></li>
              <li><router-link to="/impressum" class="text-white hover:text-secondary transition-colors font-medium">Impressum</router-link></li>
            </ul>
            <!-- Mobile Footer Navigation -->
            <ul class="md:hidden flex flex-col space-y-4 pb-4">
              <li><router-link to="/datenschutz" class="text-white hover:text-secondary transition-colors font-medium block py-2">Datenschutzerklärung</router-link></li>
              <li><router-link to="/impressum" class="text-white hover:text-secondary transition-colors font-medium block py-2">Impressum</router-link></li>
            </ul>
          </nav>
        </div>
        
        <div class="pt-6 border-t border-opacity-20 border-gray-300 text-gray-300 text-sm text-center">
          <p>&copy; {{ new Date().getFullYear() }} IT-Meeder. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import BrandNav from '@/components/BrandNav.vue'
import CookieConsent from '@/components/CookieConsent.vue'

export default {
  name: 'App',
  components: {
    BrandNav,
    CookieConsent
  },
  data() {
    return {
      mobileMenuOpen: false,
      calculatorAnimate: false,
      calculatorIntervalId: null,
      funnelReminderDismissed: false,
      isMobile: false
    };
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    dismissFunnelReminder() {
      // Funnel komplett zurücksetzen, wenn der Dialog geschlossen wird
      this.$store.dispatch('resetFunnel');
      this.funnelReminderDismissed = true;
      localStorage.setItem('funnelReminderDismissed', 'true');
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    }
  },
  computed: {
    showHeader() {
      // Header überall anzeigen
      return true;
    },
    showFooter() {
      // Footer überall anzeigen
      return true;
    },
    hasActiveFunnel() {
      // Prüfen, ob es aktive Funnel-Daten gibt und das Popup nicht ausgeblendet wurde
      // UND wenn der Funnel nicht bereits abgeschlossen wurde (Dankeseite nicht bereits besucht)
      return !this.funnelReminderDismissed &&
             this.$store.state.funnel && 
             this.$store.state.funnel.answers && 
             Object.keys(this.$store.state.funnel.answers).length > 0 && 
             !this.$store.state.funnel.completed &&
             !this.$route.path.includes('/funnel/') && // Nicht im Funnel selbst anzeigen
             !this.$route.path.includes('/thank-you'); // Nicht auf der Dankeseite anzeigen
    },
    funnelPath() {
      // Bestimme den Pfad zum letzten Funnel-Schritt
      const funnelType = this.$store.state.funnel?.type || 'business';
      const currentStep = this.$store.state.funnel?.currentStep || 1;
      return `/funnel/${funnelType}/step${currentStep}`;
    }
  },
  watch: {
    $route(to) {
      // Schließe das mobile Menü beim Routenwechsel
      this.mobileMenuOpen = false;
      
      // Wenn der Benutzer in den Funnel zurückkehrt, setze den Ausblend-Status zurück
      if (to.path.includes('/funnel/') && to.path.includes('/step')) {
        this.funnelReminderDismissed = false;
        localStorage.removeItem('funnelReminderDismissed');
      }
    }
  },
  mounted() {
    // Popup-Status aus localStorage laden
    if (localStorage.getItem('funnelReminderDismissed') === 'true') {
      this.funnelReminderDismissed = true;
    }
    
    // Prüfen, ob es sich um ein mobiles Gerät handelt
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    
    // Animation des Taschenrechnersymbols alle 10 Sekunden
    this.calculatorIntervalId = setInterval(() => {
      this.calculatorAnimate = true;
      
      // Animation nach 1 Sekunde zurücksetzen
      setTimeout(() => {
        this.calculatorAnimate = false;
      }, 1000);
    }, 10000);
  },
  beforeUnmount() {
    // Bereinige das Intervall, wenn die Komponente zerstört wird
    if (this.calculatorIntervalId) {
      clearInterval(this.calculatorIntervalId);
    }
    window.removeEventListener('resize', this.checkMobile);
  }
}
</script>

<style>
.router-link-active {
  font-weight: bold;
  border-bottom: 2px solid #fa9939;
  padding-bottom: 2px;
}

/* Verhindere Unterstreichung für das Logo */
.no-underline.router-link-active {
  border-bottom: none;
  padding-bottom: 0;
}

/* Mobile Navigation */
@media (max-width: 767px) {
  .router-link-active {
    border-left: 3px solid #fa9939;
    border-bottom: none;
    padding-left: 8px;
    padding-bottom: 0;
  }
  
  .no-underline.router-link-active {
    border-left: none;
    padding-left: 0;
  }
}

/* Desktop Navigation */
@media (min-width: 768px) {
  nav ul {
    display: flex;
    justify-content: center;
    position: absolute;
    left: 0;
    right: 0;
  }
  
  header {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
}

/* Nach oben Animation */
html {
  scroll-behavior: smooth;
}

.calculator-icon {
  transition: transform 0.3s ease, background-color 0.3s ease;
}

.calculator-icon:hover {
  transform: scale(1.1);
}

/* Vibrations- und Farb-Animation für den Taschenrechner */
@keyframes vibrate {
  0% { transform: translate(0); }
  20% { transform: translate(-2px, 2px); }
  40% { transform: translate(-2px, -2px); }
  60% { transform: translate(2px, 2px); }
  80% { transform: translate(2px, -2px); }
  100% { transform: translate(0); }
}

/* Subtile Pulsier-Animation für das Popup */
@keyframes pulse-subtle {
  0% { box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1); }
  50% { box-shadow: 0 5px 25px rgba(59, 130, 246, 0.25); }
  100% { box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1); }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s ease-in-out infinite;
}

.calculator-animate {
  animation: vibrate 0.3s ease-in-out 3;
  background-color: #fa9939 !important; /* Orange Farbe (secondary-Farbe) */
}

.calculator-animate i {
  color: white !important;
}

/* Globale Styles */
body {
  font-family: 'Inter', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Deaktiviere Textauswahl für die Navigation und andere UI-Elemente */
nav, 
.nav-item, 
button,
.btn,
.logo,
.header,
.footer {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Erlaube Textauswahl für wichtige Inhalte */
.content p,
.content h1,
.content h2,
.content h3,
.content h4,
.content h5,
.content h6,
.content li,
input,
textarea {
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}
</style> 